import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { Box } from '@mui/material'
import {
  $addUpdateTag,
  $getAdjacentNode,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isRootNode,
  $isTextNode,
  COMMAND_PRIORITY_HIGH,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_LEFT_COMMAND,
  KEY_ARROW_RIGHT_COMMAND,
  KEY_ARROW_UP_COMMAND,
  type LexicalNode,
  type PointType,
  type RangeSelection,
} from 'lexical'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { inlineEmbedEdgeRect } from '../inlineEmbedCaret'
import { $isCustomInlineEmbedNode, type CustomInlineEmbedNode } from '../nodes/CustomInlineEmbedNode'

type CaretPoint = { key: string; offset: number; type: 'text' | 'element' }

function boundaryAt(point: PointType): { node: CustomInlineEmbedNode; after: boolean } | null {
  const previous = $getAdjacentNode(point, true)
  if ($isCustomInlineEmbedNode(previous)) {
    return { node: previous, after: true }
  }
  const next = $getAdjacentNode(point, false)
  return $isCustomInlineEmbedNode(next) ? { node: next, after: false } : null
}

function outside(node: CustomInlineEmbedNode, before: boolean): CaretPoint {
  const sibling = before ? node.getPreviousSibling() : node.getNextSibling()
  if ($isTextNode(sibling)) {
    return { key: sibling.getKey(), offset: before ? sibling.getTextContentSize() : 0, type: 'text' }
  }
  return {
    key: node.getParentOrThrow().getKey(),
    offset: node.getIndexWithinParent() + (before ? 0 : 1),
    type: 'element',
  }
}

function editableEdge(node: LexicalNode, backward: boolean): CaretPoint | null {
  if ($isTextNode(node)) {
    return { key: node.getKey(), offset: backward ? node.getTextContentSize() : 0, type: 'text' }
  }
  if ($isElementNode(node)) {
    const children = node.getChildren()
    for (const child of backward ? children.reverse() : children) {
      const point = editableEdge(child, backward)
      if (point) {
        return point
      }
    }
    if (children.length === 0) {
      return { key: node.getKey(), offset: 0, type: 'element' }
    }
  }
  return null
}

/** Vertical movement skips the read-only object, landing in nearby editable prose. */
function verticalNeighbor(node: CustomInlineEmbedNode, backward: boolean): CaretPoint {
  let current: LexicalNode | null = node
  while (current && !$isRootNode(current)) {
    let sibling = backward ? current.getPreviousSibling() : current.getNextSibling()
    while (sibling) {
      const point = editableEdge(sibling, backward)
      if (point) {
        return point
      }
      sibling = backward ? sibling.getPreviousSibling() : sibling.getNextSibling()
    }
    current = current.getParent()
  }
  return outside(node, backward)
}

function moveTo(selection: RangeSelection, point: CaretPoint, extend: boolean) {
  selection.focus.set(point.key, point.offset, point.type)
  if (!extend) {
    selection.anchor.set(point.key, point.offset, point.type)
  }
}

export function InlineEmbedNavigationPlugin() {
  const [editor] = useLexicalComposerContext()
  const [caret, setCaret] = useState<{ x: number; y: number; height: number } | null>(null)

  useEffect(() => {
    const root = editor.getRootElement()
    if (!root) {
      return
    }
    let composing = false
    let frame = 0
    let revealFrame = 0
    const originalCaretColor = root.style.caretColor
    const scrollContainer = () => {
      let element = root.parentElement
      while (element && !['auto', 'scroll'].includes(getComputedStyle(element).overflowY)) {
        element = element.parentElement
      }
      return element
    }
    const measure = () =>
      editor.getEditorState().read(() => {
        const selection = $getSelection()
        if (!$isRangeSelection(selection) || document.activeElement !== root || composing) {
          return null
        }
        const boundary = boundaryAt(selection.focus)
        const dom = window.getSelection()
        const range = document.createRange()
        if (dom?.focusNode) {
          range.setStart(dom.focusNode, dom.focusOffset)
          range.collapse(true)
        }
        const native = dom?.focusNode ? range.getBoundingClientRect() : null
        const element = boundary && editor.getElementByKey(boundary.node.getKey())
        const rect = native?.height ? native : element && boundary && inlineEmbedEdgeRect(element, boundary.after)
        return rect
          ? { rect, collapsed: selection.isCollapsed(), fallback: Boolean(boundary && !native?.height) }
          : null
      })
    const refresh = () => {
      const measured = measure()
      const viewport = scrollContainer()?.getBoundingClientRect()
      const rect = measured?.rect
      const visible = Boolean(
        measured?.collapsed &&
          measured.fallback &&
          rect &&
          viewport &&
          rect.top >= viewport.top &&
          rect.bottom <= viewport.bottom,
      )
      root.style.caretColor = visible ? 'transparent' : originalCaretColor
      setCaret(visible && rect ? { x: rect.x, y: rect.y, height: rect.height } : null)
    }
    const schedule = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(refresh)
    }
    const reveal = () => {
      cancelAnimationFrame(revealFrame)
      revealFrame = requestAnimationFrame(() => {
        const measured = measure()
        const container = scrollContainer()
        if (measured && container) {
          const view = container.getBoundingClientRect()
          if (measured.rect.top < view.top + 30) {
            container.scrollTop += measured.rect.top - view.top - 30
          } else if (measured.rect.bottom > view.bottom - 30) {
            container.scrollTop += measured.rect.bottom - view.bottom + 30
          }
        }
        schedule()
      })
    }
    const move = (event: KeyboardEvent, vertical: boolean, backwardOrLeft: boolean) => {
      if (event.altKey || event.metaKey || event.ctrlKey || composing || document.activeElement !== root) {
        return false
      }
      const selection = $getSelection()
      if (!$isRangeSelection(selection) || (!selection.isCollapsed() && !event.shiftKey)) {
        return false
      }
      const boundary = boundaryAt(selection.focus)
      if (!boundary) {
        return false
      }
      let point: CaretPoint
      if (vertical) {
        // Keep native movement in editable prose, except when entering a
        // multiline read-only span: browsers can jump to the document edge.
        const dom = window.getSelection()
        if (dom?.focusNode) {
          const range = document.createRange()
          range.setStart(dom.focusNode, dom.focusOffset)
          range.collapse(true)
          if (range.getBoundingClientRect().height > 0) {
            const enteringSource = backwardOrLeft ? boundary.after : !boundary.after
            const element = editor.getElementByKey(boundary.node.getKey())
            const first = element && inlineEmbedEdgeRect(element, false)
            const last = element && inlineEmbedEdgeRect(element, true)
            if (!enteringSource || !first || !last || Math.abs(last.top - first.top) < first.height / 2) {
              return false
            }
          }
        }
        point = verticalNeighbor(boundary.node, backwardOrLeft)
      } else {
        const parentDOM = editor.getElementByKey(boundary.node.getParentOrThrow().getKey())
        const rtl = getComputedStyle(parentDOM || root).direction === 'rtl'
        const backward = rtl ? !backwardOrLeft : backwardOrLeft
        const adjacent = $getAdjacentNode(selection.focus, backward)
        if (!$isCustomInlineEmbedNode(adjacent)) {
          return false
        }
        point = outside(adjacent, backward)
      }
      event.preventDefault()
      $addUpdateTag('skip-scroll-into-view')
      moveTo(selection, point, event.shiftKey)
      reveal()
      return true
    }
    const unregister = [
      editor.registerCommand(KEY_ARROW_LEFT_COMMAND, (event) => move(event, false, true), COMMAND_PRIORITY_HIGH),
      editor.registerCommand(KEY_ARROW_RIGHT_COMMAND, (event) => move(event, false, false), COMMAND_PRIORITY_HIGH),
      editor.registerCommand(KEY_ARROW_UP_COMMAND, (event) => move(event, true, true), COMMAND_PRIORITY_HIGH),
      editor.registerCommand(KEY_ARROW_DOWN_COMMAND, (event) => move(event, true, false), COMMAND_PRIORITY_HIGH),
      editor.registerUpdateListener(schedule),
    ]
    const compositionStart = () => {
      composing = true
      schedule()
    }
    const compositionEnd = () => {
      composing = false
      schedule()
    }
    const observer = new MutationObserver(schedule)
    observer.observe(root, { childList: true, characterData: true, subtree: true })
    document.addEventListener('selectionchange', schedule)
    document.addEventListener('focusin', schedule)
    window.addEventListener('scroll', schedule, true)
    window.addEventListener('resize', schedule)
    root.addEventListener('compositionstart', compositionStart)
    root.addEventListener('compositionend', compositionEnd)
    schedule()
    return () => {
      unregister.forEach((remove) => remove())
      observer.disconnect()
      cancelAnimationFrame(frame)
      cancelAnimationFrame(revealFrame)
      document.removeEventListener('selectionchange', schedule)
      document.removeEventListener('focusin', schedule)
      window.removeEventListener('scroll', schedule, true)
      window.removeEventListener('resize', schedule)
      root.removeEventListener('compositionstart', compositionStart)
      root.removeEventListener('compositionend', compositionEnd)
      root.style.caretColor = originalCaretColor
    }
  }, [editor])

  return caret
    ? createPortal(
        <Box
          aria-hidden="true"
          data-inline-embed-caret="true"
          sx={{
            position: 'fixed',
            pointerEvents: 'none',
            left: caret.x,
            top: caret.y,
            height: caret.height,
            width: '1px',
            bgcolor: 'text.primary',
            zIndex: 1,
            animation: 'inlineCaretBlink 1s step-end infinite',
            '@keyframes inlineCaretBlink': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0 } },
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        />,
        document.body,
      )
    : null
}
